import { Fragment, type ReactNode } from "react";

import { BrandMark } from "./BrandMark";

/* ==========================================================================
   ONE shell for the three product sites.

   The reference header, permanently glass, with a brand block at every width
   and on every route — including sign-in, which is the first screen most people
   ever see and which used to carry no Shoku identity at all. Below 860px the
   same `.nav-links` element re-positions as a bottom tab bar: one DOM, two
   positions, every link labelled at both.

   The shell renders NO heading. A workspace that prints "Earnings" in the top
   bar and "Earnings" again 60px below is the strongest "unfinished" signal a
   product can send, and when the two disagree ("Find jobs" over "Leaking
   kitchen tap") it is worse than saying nothing. `<PageHead>` owns the h1.

   This component is presentational. Routing is the site's business, so links
   are rendered through `renderLink` — pass react-router's Link, or omit it and
   get plain anchors.
   ========================================================================== */

export interface ShellLink {
  href: string;
  label: string;
  /** Shown below 860px, hidden above; the label is always present. */
  icon?: ReactNode;
  /**
   * The first path segment this link owns. `/jobs/job-1` and `/jobs` both light
   * "Find jobs", which is what stops every detail route from looking like it
   * belongs to no section at all.
   */
  section: string;
  /**
   * An unread count, rendered beside the label. `.icon-btn__dot` is the shape
   * — the same pill the bell wears — and the tab is `position: relative` so it
   * anchors there. The shell does not count anything: the site knows what is
   * unread, the shell only makes room for it.
   */
  badge?: ReactNode;
}

export type ShellVariant = "app" | "auth";

/**
 * How wide the page column is.
 *
 * The reference container is 78rem, which is right for a grid of cards and far
 * too wide for a form, a spec sheet or a list of rows — a screen that is one
 * column should say so, or the eye has to travel 1100px between a section label
 * and the button beside it.
 */
export type ShellWidth = "wide" | "narrow";

export interface RenderLinkProps {
  href: string;
  current: boolean;
  children: ReactNode;
}

export interface ShellProps {
  children: ReactNode;
  /** `auth` = brand + footer only: sign-in, register, 404, the map document. */
  variant?: ShellVariant;
  /** `narrow` caps the column at 48rem. `auth` is always narrower still. */
  width?: ShellWidth;
  /**
   * This screen FITS the viewport instead of flowing down the page.
   *
   * One shape qualifies: a chat, whose composer has to stay put while the log
   * scrolls. The shell stops padding a band of page under the screen and hands
   * it the exact leftover between the header and the footer, so the screen's
   * own root can be `flex: 1` rather than subtracting a guess at the chrome.
   */
  fit?: boolean;
  brandHref?: string;
  links?: readonly ShellLink[];
  /** The FIRST path segment, so a detail route still lights its section. */
  currentSection?: string;
  /** Bell, avatar menu — whatever the site's account cluster is. */
  account?: ReactNode;
  /** At most one, and only for the site's single global action. */
  cta?: ReactNode;
  renderLink?: (props: RenderLinkProps) => ReactNode;
  /** Same reason as `renderLink`: the brand is a link home, and on a client-
   *  routed app a plain anchor there costs a full reload of the whole bundle. */
  renderBrand?: (props: { href: string; className: string; children: ReactNode }) => ReactNode;
  footerLinks?: readonly { href: string; label: string }[];
}

/**
 * The section a path belongs to: its FIRST segment.
 *
 * `/settings/services/svc-1` is "settings", so a bookable contractor editing a
 * service still sees Booking setup lit. Matching on the whole path is how the
 * old shell ended up with detail routes that highlighted nothing at all.
 */
export function sectionOf(pathname: string): string {
  return pathname.split("/").filter(Boolean)[0] ?? "";
}

const DEFAULT_FOOTER_LINKS = [
  { href: "https://shoku-prod.web.app/privacy", label: "Privacy" },
  { href: "https://shoku-prod.web.app/terms", label: "Terms" },
  { href: "mailto:support@shoku.co.za", label: "Support" },
] as const;

export function Shell({
  children,
  variant = "app",
  width = "wide",
  fit = false,
  brandHref = "/",
  links = [],
  currentSection,
  account,
  cta,
  renderLink,
  renderBrand,
  footerLinks = DEFAULT_FOOTER_LINKS,
}: ShellProps) {
  const showNav = variant === "app" && links.length > 0;
  const brand = (
    <>
      <BrandMark />
      <span>Shoku</span>
    </>
  );

  return (
    <>
      <div className="grain" aria-hidden="true" />
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="site-header site-header--app">
        <div className="container">
          <nav className="nav" aria-label="Primary">
            {renderBrand ? (
              renderBrand({ href: brandHref, className: "brand", children: brand })
            ) : (
              <a className="brand" href={brandHref}>
                {brand}
              </a>
            )}

            {showNav ? (
              <div className="nav-links">
                {links.map((link) => {
                  const current = currentSection === link.section;
                  const content = (
                    <>
                      {link.icon}
                      <span>{link.label}</span>
                      {link.badge}
                    </>
                  );
                  if (renderLink) {
                    return (
                      <Fragment key={link.href}>
                        {renderLink({ href: link.href, current, children: content })}
                      </Fragment>
                    );
                  }
                  return (
                    <a
                      key={link.href}
                      href={link.href}
                      aria-current={current ? "page" : undefined}
                    >
                      {content}
                    </a>
                  );
                })}
                {cta}
              </div>
            ) : null}

            {variant === "app" && account ? <div className="nav-account">{account}</div> : null}
          </nav>
        </div>
      </header>

      <main
        className={`app-main${variant === "auth" ? " app-main--auth" : fit ? " app-main--fit" : ""}`}
        id="main"
        // The route-change focus target of last resort. The page head's h1 is
        // the real one; this catches a route that is still loading when the
        // navigation lands and therefore has no heading yet.
        tabIndex={-1}
      >
        {/* Glass with nothing behind it is just a lighter band of page: this is
            what the header blurs on every route. */}
        <div className="glow glow--primary app-glow" aria-hidden="true" />
        <div className={width === "narrow" ? "container narrow" : "container"}>{children}</div>
      </main>

      <AppFooter links={footerLinks} />

      {/* Only when there IS a bar to clear — see .tabbar-spacer in app.css. An
          auth route has no tab bar and must not carry a 4rem hole below it. */}
      {showNav ? <div className="tabbar-spacer" aria-hidden="true" /> : null}
    </>
  );
}

export function AppFooter({
  links = DEFAULT_FOOTER_LINKS,
}: {
  links?: readonly { href: string; label: string }[];
}) {
  return (
    <footer className="site-footer site-footer--app">
      <div className="container">
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} Shoku</span>
          <span className="cluster cluster--tight">
            {links.map((link) => (
              <a key={link.href} href={link.href}>
                {link.label}
              </a>
            ))}
          </span>
        </div>
      </div>
    </footer>
  );
}
